/*
 * Licensed under the Apache License, Version 2.0 (the "License"); you may not use
 * this file except in compliance with the License. You may obtain a copy of the
 * License at
 *
 *  https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed
 * under the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR
 * CONDITIONS OF ANY KIND, either express or implied. See the License for the
 * specific language governing permissions and limitations under the License.
 */
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import Box from "@mui/material/Box";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Fade from "@mui/material/Fade";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Popper, { PopperPlacementType, PopperProps } from "@mui/material/Popper";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import IconClose from "@mui/icons-material/Close";
import IconInfo from "@mui/icons-material/Info";
import IconInfoOutlined from "@mui/icons-material/InfoOutlined";

import type { Theme } from "@mui/material/styles";
import { SHORT_VIEWPORT_QUERY } from "../../theme";
import { wordBreakSx } from "../../utils";
import {
  LAYER_INFO_FONT_SIZE,
  layerInfoChipSx,
  layerInfoHeadingValuesSx,
  layerInfoTableSx,
  layerInfoTextSx,
} from "./layerInfoStyles";
import {
  DIALOG_CONTENT_HEADING_SELECTOR,
  NarrowDialogFallback,
} from "./NarrowDialogFallback";
import { useInfoPopoverGroup } from "./InfoPopoverGroup";
import { ARROW_SIZE, VIEWPORT_MARGIN } from "./popperConstants";
import {
  buildArrowModifier,
  buildOffsetModifier,
  buildPreventOverflowModifier,
  buildFlipModifier,
} from "./popperModifiers";
import { useAvailableSpace } from "./useAvailableSpace";

interface IInfoPopoverProps {
  open: boolean;
  onClose: (event?: any, reason?: string) => void;
  anchorEl: PopperProps["anchorEl"];
  title?: React.ReactNode;
  children?: React.ReactNode;
  placement?: PopperPlacementType;
  popperMaxWidth?: number | string;
  /** Close the popover when the user clicks outside of it. Default false. */
  closeOnClickAway?: boolean;
  /**
   * DOM element whose contents should NOT trigger click-away close. Useful
   * when `anchorEl` is a virtual element but the user can still click the
   * real anchor (e.g. the launcher icon) and we don't want that to close
   * and immediately re-open the popover.
   */
  clickAwayExclude?: HTMLElement | null;
  /**
   * When provided, the arrow tracks this element's vertical/horizontal
   * centre instead of pointing at `anchorEl`. Use this when the popover is
   * locked to a fixed position but the arrow should still visually indicate
   * the launcher that opened it.
   */
  arrowAnchorEl?: HTMLElement | null;
}

// Title / content / actions spacing & typography for the desktop popover
// Paper. Pulled out of the inline sx for readability.
const popoverDialogPartsSx = (theme: Theme) => ({
  "&& .MuiDialogTitle-root": {
    ...theme.typography.subtitle2,
    // theme.typography.subtitle2 doesn't define a colour, so set it
    // explicitly to match the legend's layer-title h3.
    color: theme.palette.text.secondary,
    fontSize: "0.9rem !important",
    fontWeight: 400,
    lineHeight: 1.3,
    padding: theme.spacing(2, 6, 1, 2.5),
  },
  "&& .MuiDialogContent-root": {
    fontSize: LAYER_INFO_FONT_SIZE,
    padding: theme.spacing(0, 2.5, 2, 2.5),
  },
});

function InfoPopover(props: IInfoPopoverProps) {
  const {
    open,
    onClose,
    anchorEl,
    title,
    children,
    placement = "top",
    popperMaxWidth = 400,
    closeOnClickAway = false,
    clickAwayExclude = null,
    arrowAnchorEl = null,
  } = props;
  const { t } = useTranslation();
  const theme = useTheme();
  // Fall back to the full-screen Dialog at sm-and-below widths (where the
  // popover would otherwise overlap the sidebar) or short heights (mobile
  // landscape) where the popover isn't usable.
  const isNarrowWidth = useMediaQuery(theme.breakpoints.down("md"));
  const isShortHeight = useMediaQuery(SHORT_VIEWPORT_QUERY);
  const isNarrow = isNarrowWidth || isShortHeight;
  const [arrowRef, setArrowRef] = useState<HTMLSpanElement | null>(null);
  const [paperEl, setPaperEl] = useState<HTMLDivElement | null>(null);
  const [arrowTop, setArrowTop] = useState<number | null>(null);
  const popperRef = useRef<any>(null);

  const { width: availableWidth, height: availableHeight } = useAvailableSpace(
    anchorEl,
    placement,
    open && !isNarrow
  );

  useEffect(() => {
    if (!open || isNarrow) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose(event, "escapeKeyDown");
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, isNarrow, onClose]);

  // Keep the arrow pointing at `arrowAnchorEl` as the user scrolls/resizes.
  useEffect(() => {
    if (!open || isNarrow || !arrowAnchorEl || !paperEl) {
      // Intentionally don't reset arrowTop here — resetting would cause the
      // arrow to animate back to its default position while the popover is
      // fading out, making it appear to linger after the popover closes.
      return;
    }
    const update = () => {
      // Trigger Popper to re-run its modifiers (including the offset
      // modifier that shifts the popover to track the icon). Popper only
      // listens to scroll on the popper's own scroll parents, not the
      // anchor's, so we have to nudge it manually.
      popperRef.current?.update();
      const iconRect = arrowAnchorEl.getBoundingClientRect();
      const paperRect = paperEl.getBoundingClientRect();
      if (paperRect.height === 0) return;
      const center = iconRect.top + iconRect.height / 2;
      const arrowY = center - paperRect.top - ARROW_SIZE;
      const minTop = 4;
      const maxTop = paperRect.height - ARROW_SIZE * 2 - 4;
      setArrowTop(Math.max(minTop, Math.min(arrowY, maxTop)));
    };
    // Run once now, and again on the next frame after Popper has placed
    // the popover — otherwise the first measurement uses a stale rect.
    update();
    const initialRaf = requestAnimationFrame(() => {
      update();
      requestAnimationFrame(update);
    });
    const rafUpdate = () => requestAnimationFrame(update);
    window.addEventListener("scroll", rafUpdate, true);
    window.addEventListener("resize", rafUpdate);
    return () => {
      cancelAnimationFrame(initialRaf);
      window.removeEventListener("scroll", rafUpdate, true);
      window.removeEventListener("resize", rafUpdate);
    };
  }, [open, isNarrow, arrowAnchorEl, paperEl]);

  const dialogBody = (
    <>
      {title != null && <DialogTitle>{title}</DialogTitle>}
      <IconButton
        size="small"
        aria-label={t("Close")}
        className="marapp-qa-modalclose"
        onClick={(e) => onClose(e, "closeButton")}
        sx={{
          position: "absolute",
          top: theme.spacing(1),
          right: theme.spacing(1),
          zIndex: 1,
        }}
      >
        <IconClose fontSize="small" />
      </IconButton>
      <DialogContent
        sx={{
          ...wordBreakSx,
          ...layerInfoTableSx,
          flex: "1 1 auto",
          minHeight: 0,
          overflow: "auto",
        }}
      >
        {children}
      </DialogContent>
    </>
  );

  if (isNarrow) {
    return (
      <NarrowDialogFallback open={open} onClose={onClose}>
        {dialogBody}
      </NarrowDialogFallback>
    );
  }

  // Match the dialog background so the arrow visually merges with the
  // popover body.
  const arrowColor = theme.palette.background.paper;
  const numericMaxWidth =
    typeof popperMaxWidth === "number" ? popperMaxWidth : null;
  const widthCss =
    availableWidth != null && numericMaxWidth != null
      ? `${Math.min(numericMaxWidth, availableWidth)}px`
      : typeof popperMaxWidth === "number"
      ? `min(${popperMaxWidth}px, calc(100vw - ${VIEWPORT_MARGIN * 2}px))`
      : `min(${popperMaxWidth}, calc(100vw - ${VIEWPORT_MARGIN * 2}px))`;
  const heightCss =
    availableHeight != null
      ? `${availableHeight}px`
      : `calc(100vh - ${VIEWPORT_MARGIN * 2}px)`;

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement={placement}
      transition
      popperRef={popperRef}
      sx={{
        zIndex: theme.zIndex.modal,
        '&[data-popper-placement*="top"] .info-popover-arrow': {
          bottom: -(ARROW_SIZE / 2 + 10),
          "&::before": {
            borderWidth: `${ARROW_SIZE}px ${ARROW_SIZE}px 0 ${ARROW_SIZE}px`,
            borderColor: `${arrowColor} transparent transparent transparent`,
            // Cast the shadow downward, away from the body above.
            filter: "drop-shadow(0 2px 2px rgba(0,0,0,0.3))",
          },
        },
        '&[data-popper-placement*="bottom"] .info-popover-arrow': {
          top: -(ARROW_SIZE / 2 + 2),
          "&::before": {
            borderWidth: `0 ${ARROW_SIZE}px ${ARROW_SIZE}px ${ARROW_SIZE}px`,
            borderColor: `transparent transparent ${arrowColor} transparent`,
            // Cast the shadow upward, away from the body below.
            filter: "drop-shadow(0 -2px 2px rgba(0,0,0,0.3))",
          },
        },
        '&[data-popper-placement*="right"] .info-popover-arrow': {
          left: -(ARROW_SIZE / 2 + 8),
          "&::before": {
            borderWidth: `${ARROW_SIZE}px ${ARROW_SIZE}px ${ARROW_SIZE}px 0`,
            borderColor: `transparent ${arrowColor} transparent transparent`,
            // Cast the shadow leftward, away from the body to the right.
            filter: "drop-shadow(-2px 0 2px rgba(0,0,0,0.3))",
          },
        },
        '&[data-popper-placement*="left"] .info-popover-arrow': {
          right: -(ARROW_SIZE / 2 + 14),
          "&::before": {
            borderWidth: `${ARROW_SIZE}px 0 ${ARROW_SIZE}px ${ARROW_SIZE}px`,
            borderColor: `transparent transparent transparent ${arrowColor}`,
            // Cast the shadow rightward, away from the body to the left.
            filter: "drop-shadow(2px 0 2px rgba(0,0,0,0.3))",
          },
        },
      }}
      modifiers={[
        buildArrowModifier(arrowRef, arrowAnchorEl),
        buildOffsetModifier(anchorEl, arrowAnchorEl),
        buildPreventOverflowModifier(placement),
        buildFlipModifier(placement),
      ]}
    >
      {({ TransitionProps }) => {
        const paper = (
          <Box
            sx={{
              position: "relative",
              width: widthCss,
              maxWidth: widthCss,
            }}
          >
            <Paper
              ref={setPaperEl}
              elevation={24}
              sx={{
                position: "relative",
                width: widthCss,
                maxWidth: widthCss,
                height: "auto",
                maxHeight: heightCss,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                backgroundColor: theme.palette.background.paper,
                boxShadow:
                  "0 12px 32px -8px rgba(0,0,0,0.55), 0 4px 12px rgba(0,0,0,0.3)",
                "& > *": {
                  backgroundColor: "inherit",
                },
                // Scale down text inside the popover for a tighter, more
                // compact look that fits the smaller bubble. High-specificity
                // selectors in `layerInfoTextSx` ensure nested children (e.g.
                // inside ConsolidatedDescription's Html block) inherit.
                ...layerInfoTextSx,
                ...popoverDialogPartsSx(theme),
                [DIALOG_CONTENT_HEADING_SELECTOR]:
                  layerInfoHeadingValuesSx(theme),
                ...layerInfoChipSx,
              }}
              onKeyDown={(event) => {
                if (event.key === "Escape") {
                  event.stopPropagation();
                  onClose(event, "escapeKeyDown");
                }
              }}
            >
              {dialogBody}
            </Paper>
            <Box
              component="span"
              ref={setArrowRef}
              className="info-popover-arrow"
              sx={{
                position: "absolute",
                width: ARROW_SIZE * 2,
                height: ARROW_SIZE * 2,
                pointerEvents: "none",
                ...(arrowTop !== null && {
                  top: `${arrowTop}px !important`,
                  bottom: "auto !important",
                  transition: "top 0.1s linear",
                }),
                "&::before": {
                  content: '""',
                  display: "block",
                  margin: "auto",
                  width: 0,
                  height: 0,
                  borderStyle: "solid",
                },
              }}
            />
          </Box>
        );

        return (
          <Fade {...TransitionProps} timeout={150}>
            {closeOnClickAway ? (
              <Box>
                <ClickAwayListener
                  mouseEvent="onMouseDown"
                  onClickAway={(event) => {
                    const target = event.target as Node | null;
                    const excludeNode =
                      clickAwayExclude ??
                      (typeof anchorEl === "function" ? anchorEl() : anchorEl);
                    if (
                      excludeNode instanceof Element &&
                      target &&
                      excludeNode.contains(target)
                    ) {
                      return;
                    }
                    onClose(event, "clickAway");
                  }}
                >
                  {paper}
                </ClickAwayListener>
              </Box>
            ) : (
              paper
            )}
          </Fade>
        );
      }}
    </Popper>
  );
}

export interface IInfoPopoverLauncherProps {
  title?: React.ReactNode;
  children?: React.ReactNode;
  iconButtonProps?: React.ComponentProps<typeof IconButton>;
  placement?: PopperPlacementType;
  popperMaxWidth?: number | string;
  /**
   * CSS selector for an element to use as the popover's *position* anchor.
   * When set, every launcher anchored to the same element opens the popover
   * in the same screen location; the arrow tracks the actual launcher icon.
   */
  fixedAnchorSelector?: string;
  /** Close the popover when the user clicks outside of it. Default true. */
  closeOnClickAway?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
}

export interface IInfoPopoverLauncherRef {
  close: () => void;
}

interface VirtualAnchor {
  getBoundingClientRect: () => DOMRect;
}

export const InfoPopoverLauncher = forwardRef<
  IInfoPopoverLauncherRef,
  IInfoPopoverLauncherProps
>(function InfoPopoverLauncher(props, ref) {
  const {
    title,
    children,
    iconButtonProps,
    placement,
    popperMaxWidth,
    fixedAnchorSelector,
    closeOnClickAway = true,
    onOpen,
    onClose,
  } = props;
  const [iconEl, setIconEl] = useState<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);
  const [staticAnchor, setStaticAnchor] = useState<VirtualAnchor | null>(null);
  const group = useInfoPopoverGroup();

  const handleClose = useCallback(() => {
    setOpen(false);
    // Intentionally don't clear staticAnchor here — clearing it mid-
    // transition makes Popper lose its anchor and the offset modifier
    // snap back to 0, which visually jumps the popover before it fades.
    // It will be overwritten on the next open.
    onClose?.();
  }, [onClose]);

  useImperativeHandle(ref, () => ({ close: handleClose }), [handleClose]);

  // Register this launcher's "force close" callback with the surrounding
  // group so other launchers in the same group can close us when they open.
  const forceClose = useCallback(() => {
    setOpen(false);
  }, []);

  useEffect(() => {
    return group.register(forceClose);
  }, [group, forceClose]);

  const handleToggle = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (open) {
      handleClose();
      return;
    }
    // Prefer a fixed-position anchor (e.g. the sidebar) so the popover
    // always opens in the same spot. Fall back to the launcher icon itself
    // when no fixed anchor is provided or the selector doesn't match.
    const fixedAnchor = fixedAnchorSelector
      ? (document.querySelector(fixedAnchorSelector) as HTMLElement | null)
      : null;
    if (fixedAnchor) {
      const rect = fixedAnchor.getBoundingClientRect();
      // If the anchor has scrolled above the viewport (rect.top < 0), clamp
      // it so the popover doesn't open above the viewport top. Also nudge
      // it down a touch so there's a visible gap above the popover.
      const MIN_TOP = 16;
      const TOP_OFFSET = 40;
      const clampedTop = Math.max(MIN_TOP, rect.top) + TOP_OFFSET;
      const adjustedRect = new DOMRect(
        rect.left,
        clampedTop,
        rect.width,
        rect.height
      );
      setStaticAnchor({ getBoundingClientRect: () => adjustedRect });
    } else {
      // No fixed anchor: anchor directly to the live launcher icon so the
      // popover opens next to it (and tracks it on scroll/resize). The
      // sidebar's top-clamp/offset nudge would push it away from the icon,
      // so it's intentionally not applied here.
      if (!iconEl) return;
      setStaticAnchor(null);
    }
    setOpen(true);
    onOpen?.();
    // Close any other launcher in this group that's currently open.
    group.closeOthers(forceClose);
  };

  // Close the popover when the launcher icon scrolls out of view.
  useEffect(() => {
    if (!open || !iconEl) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting) {
          handleClose();
        }
      },
      { threshold: 0 }
    );
    observer.observe(iconEl);
    return () => observer.disconnect();
  }, [open, iconEl, handleClose]);

  return (
    <>
      <IconButton
        size="small"
        {...iconButtonProps}
        ref={setIconEl}
        onClick={handleToggle}
      >
        {open ? (
          <IconInfo fontSize="small" />
        ) : (
          <IconInfoOutlined
            fontSize="small"
            sx={{ color: "rgba(255, 255, 255, 0.7)" }}
          />
        )}
      </IconButton>
      <InfoPopover
        open={open}
        onClose={handleClose}
        anchorEl={staticAnchor ?? iconEl}
        clickAwayExclude={iconEl}
        arrowAnchorEl={fixedAnchorSelector ? iconEl : null}
        title={title}
        placement={placement}
        popperMaxWidth={popperMaxWidth}
        closeOnClickAway={closeOnClickAway}
      >
        {children}
      </InfoPopover>
    </>
  );
});
